import { useEffect, useRef, useState } from "react";
import { apiJson } from "../../../shared/lib/api-client.ts";

const REDOC_CDN_URL = "https://cdn.jsdelivr.net/npm/redoc@2.1.5/bundles/redoc.standalone.min.js";

declare global {
  interface Window {
    Redoc?: {
      init: (
        spec: unknown,
        options: Record<string, unknown>,
        element: HTMLElement,
        callback?: (err?: Error) => void
      ) => void;
    };
  }
}

async function ensureRedocLoaded(): Promise<void> {
  if (window.Redoc) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${REDOC_CDN_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Redoc")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = REDOC_CDN_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Redoc from CDN"));
    document.head.appendChild(script);
  });
}

export function ApiDocsView() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        setStatus("loading");
        setError(null);

        const [spec] = await Promise.all([
          apiJson<unknown>("/v1/openapi.json"),
          ensureRedocLoaded(),
        ]);

        if (cancelled) return;
        if (!containerRef.current) return;
        if (!window.Redoc) {
          throw new Error("Redoc did not load");
        }

        // Clear any previous render (e.g. StrictMode double-invoke)
        containerRef.current.innerHTML = "";

        // Comprehensive dark theme for Redoc — matches the Legion Scout shell.
        // Every text/background color is explicit so nothing falls back to
        // Redoc's light defaults (which were causing the gray-on-gray issue).
        const TEXT_PRIMARY = "#e2e8f0"; // light slate
        const TEXT_SECONDARY = "#94a3b8"; // muted slate
        const BG_PANEL = "#1e293b"; // mid slate
        const BG_CODE = "#0b1120"; // darker than page bg
        const BORDER = "#334155";
        const ACCENT = "#a855f7"; // purple

        window.Redoc.init(
          spec,
          {
            theme: {
              colors: {
                primary: { main: ACCENT },
                success: { main: "#22c55e" },
                warning: { main: "#f59e0b" },
                error: { main: "#ef4444" },
                text: {
                  primary: TEXT_PRIMARY,
                  secondary: TEXT_SECONDARY,
                },
                border: {
                  dark: BORDER,
                  light: BORDER,
                },
                http: {
                  get: "#10b981",
                  post: "#3b82f6",
                  put: "#f59e0b",
                  patch: "#8b5cf6",
                  delete: "#ef4444",
                  options: "#94a3b8",
                  head: "#94a3b8",
                  basic: "#94a3b8",
                  link: "#a855f7",
                },
              },
              typography: {
                fontSize: "14px",
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                smoothing: "antialiased",
                headings: {
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontWeight: "600",
                },
                code: {
                  fontSize: "13px",
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                  color: "#e879f9",
                  backgroundColor: BG_CODE,
                  wrap: true,
                },
                links: {
                  color: ACCENT,
                  visited: ACCENT,
                  hover: "#c084fc",
                },
              },
              sidebar: {
                width: "280px",
                backgroundColor: BG_PANEL,
                textColor: TEXT_PRIMARY,
                arrow: { color: TEXT_SECONDARY },
              },
              rightPanel: {
                backgroundColor: BG_CODE,
                textColor: TEXT_PRIMARY,
              },
              schema: {
                nestedBackground: BG_PANEL,
                linesColor: BORDER,
                typeNameColor: "#a855f7",
                typeTitleColor: TEXT_PRIMARY,
                requireLabelColor: "#f87171",
                labelsTextSize: "13px",
                nestingSpacing: "1em",
                arrow: { size: "1.1em", color: TEXT_SECONDARY },
              },
              codeBlock: {
                backgroundColor: BG_CODE,
                tokens: {},
              },
              fab: {
                backgroundColor: ACCENT,
                color: "#ffffff",
              },
            },
            hideDownloadButton: false,
            disableSearch: false,
            nativeScrollbars: true,
            expandResponses: "200,201",
            jsonSampleExpandLevel: 2,
            menuToggle: true,
          },
          containerRef.current,
          (err?: Error) => {
            if (cancelled) return;
            if (err) {
              setError(err.message);
              setStatus("error");
            } else {
              setStatus("ready");
            }
          }
        );
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        // Fill viewport minus the fixed header (5rem) and the wrapper's
        // own padding so Redoc's internal sticky sidebar/right-panel layout
        // has a definite height to anchor against.
        height: "calc(100vh - 6.5rem)",
        background: "#0f172a",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #1e293b",
      }}
    >
      {status === "loading" && (
        <div style={{ padding: 24, color: "#94a3b8" }}>Loading API documentation…</div>
      )}
      {status === "error" && (
        <div
          style={{
            padding: 24,
            color: "#ef4444",
            background: "#1e1e2e",
            border: "1px solid #ef4444",
            borderRadius: 8,
          }}
        >
          Failed to load API docs: {error}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          display: status === "ready" ? "block" : "none",
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      />
    </div>
  );
}
